/** Sign-in in two steps: identifier (e-mail or WhatsApp number), then the one-time code.
 * Design v2.1: a split screen over the vivid backdrop — what AgencyOS does on the left, the glass
 * card on the right (stacked on a phone). */

import { ApiError, academyMeRetrieve, useAuthOtpRequestCreate, useAuthOtpVerifyCreate } from "@agencyos/api-client";
import { isValidIdentifier } from "@agencyos/core";
import { Backdrop, Button, GlassCard, TextField } from "@agencyos/ui-web";
import { FileCheck2, MessageSquareText, ReceiptText } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { OfflineNotice } from "../components/AppShell";
import { LanguageSwitcher, ThemeSwitcher } from "../components/Preferences";
import { session } from "../lib/session";

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"identifier" | "code">("identifier");
  const [error, setError] = useState<string | null>(null);
  const requestCode = useAuthOtpRequestCreate();
  const verifyCode = useAuthOtpVerifyCreate();

  const explain = (failure: unknown): string => {
    if (failure instanceof ApiError && failure.status === 429) return t("auth.locked");
    if (failure instanceof ApiError && failure.status === 400) return t("auth.wrongCode");
    // No answer or a server failure: say so, instead of a vague error.
    if (!(failure instanceof ApiError) || failure.status >= 500) return t("auth.serverDown");
    return t("common.error");
  };

  const onRequest = (event: FormEvent) => {
    event.preventDefault();
    if (!isValidIdentifier(identifier)) {
      setError(t("auth.invalidIdentifier"));
      return;
    }
    setError(null);
    requestCode.mutate(
      { data: { identifier } },
      { onSuccess: () => setStep("code"), onError: (failure) => setError(explain(failure)) },
    );
  };

  const onVerify = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    verifyCode.mutate(
      { data: { identifier, code } },
      {
        onSuccess: (response) => {
          if (response.status !== 200) return;
          const { access, user } = response.data;
          session.signIn(access, user.memberships[0]?.organization_slug ?? null);
          if (user.memberships.length || user.is_superuser) {
            navigate("/", { replace: true });
            return;
          }
          // D-123: an Academy learner who signs in here (no agency) goes to their space; they
          // landed on an empty back office (« Votre compte n'est rattaché à aucune agence »)
          void academyMeRetrieve()
            .then((me) => navigate(me.status === 200 ? "/academy/home" : "/", { replace: true }))
            .catch(() => navigate("/", { replace: true }));
        },
        onError: (failure) => setError(explain(failure)),
      },
    );
  };

  return (
    <>
      <Backdrop intensity="vivid" />
      <main className="login login--split">
        <div className="login-tools">
          <LanguageSwitcher />
          <ThemeSwitcher />
        </div>
        <section className="login-hero" aria-labelledby="login-hero-title">
          <span className="login-hero__mark" aria-hidden="true">
            {t("app.name").charAt(0)}
          </span>
          <p className="login-hero__title" id="login-hero-title">
            {t("app.tagline")}
          </p>
          <p className="login-hero__lead">{t("auth.heroLead")}</p>
          <ul className="login-hero__points">
            <li>
              <MessageSquareText size={18} strokeWidth={1.75} aria-hidden="true" />
              {t("auth.heroPoints.inbox")}
            </li>
            <li>
              <FileCheck2 size={18} strokeWidth={1.75} aria-hidden="true" />
              {t("auth.heroPoints.files")}
            </li>
            <li>
              <ReceiptText size={18} strokeWidth={1.75} aria-hidden="true" />
              {t("auth.heroPoints.money")}
            </li>
          </ul>
        </section>
        <GlassCard level="thick" className="login-card">
          <OfflineNotice />
          <p className="login-brand">{t("app.name")}</p>
          <h1>{t("auth.title")}</h1>
          <p className="muted">{t("auth.subtitle")}</p>

          {step === "identifier" ? (
            <form onSubmit={onRequest} className="stack" noValidate>
              <TextField
                label={t("auth.identifierLabel")}
                placeholder={t("auth.identifierPlaceholder")}
                hint={t("auth.identifierHint")}
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                autoComplete="username"
                inputMode="email"
                error={error ?? undefined}
                required
              />
              <Button type="submit" block disabled={requestCode.isPending}>
                {requestCode.isPending ? t("common.loading") : t("auth.sendCode")}
              </Button>
            </form>
          ) : (
            <form onSubmit={onVerify} className="stack">
              <p role="status">{t("auth.codeSent", { identifier })}</p>
              <TextField
                label={t("auth.codeLabel")}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={10}
                error={error ?? undefined}
                required
              />
              <Button type="submit" block disabled={verifyCode.isPending || code.length < 4}>
                {verifyCode.isPending ? t("common.loading") : t("auth.verify")}
              </Button>
              <div className="row">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setStep("identifier");
                    setCode("");
                    setError(null);
                  }}
                >
                  {t("auth.changeIdentifier")}
                </Button>
                <Button variant="ghost" onClick={() => requestCode.mutate({ data: { identifier } })}>
                  {t("auth.resend")}
                </Button>
              </div>
            </form>
          )}
          <p className="login-legal">{t("auth.legal")}</p>
        </GlassCard>
      </main>
    </>
  );
}
